/**
 * Shared Kanban layout class recipes.
 */

import { cn } from '@/utils/cn';
import type { KanbanDensity } from './Kanban.config.types';

export const COLUMN_SORT_ID_PREFIX = 'column-sort:';

export function toColumnSortId(columnId: string): string {
  return `${COLUMN_SORT_ID_PREFIX}${columnId}`;
}

export function fromColumnSortId(id: string): string | null {
  if (!id.startsWith(COLUMN_SORT_ID_PREFIX)) return null;
  return id.slice(COLUMN_SORT_ID_PREFIX.length);
}

export const kanbanShellClass =
  'h-full bg-white flex flex-col overflow-hidden';

export const kanbanBoardScrollClass =
  'flex-1 min-h-0 overflow-x-auto overflow-y-hidden';

export const kanbanBoardRowClass =
  'flex h-full py-4 px-2 min-w-max items-stretch';

/** Default column spacing when insert zones are not providing the gap. */
export const kanbanBoardRowGapClass = 'gap-3';

export function getKanbanColumnClass(options: {
  isDropTarget?: boolean;
  isHighlighted?: boolean;
  isColumnDragging?: boolean;
  isCollapsed?: boolean;
}): string {
  const { isDropTarget, isHighlighted, isColumnDragging, isCollapsed } =
    options;
  return cn(
    'group/column flex flex-col transition-[width,colors] duration-200',
    isCollapsed
      ? 'w-12 min-w-12 max-w-12 self-stretch h-full min-h-0 rounded-full bg-dark-invirtus-soft/40 border border-dark-invirtus-soft/70'
      : 'min-w-[272px] w-[272px] max-w-[300px] self-stretch h-full min-h-0 rounded-2xl bg-dark-invirtus-soft/35 border border-transparent',
    isHighlighted && !isDropTarget && 'border-blue/25 bg-blue/[0.04]',
    isDropTarget && 'border-blue/45 bg-blue/10 ring-2 ring-blue/15',
    isColumnDragging && 'opacity-40'
  );
}

export const kanbanColumnHeaderClass =
  'flex items-center gap-1.5 shrink-0 pt-1 pb-2';

export const kanbanColumnHeaderCollapsedClass =
  'flex flex-col items-center gap-2 px-1.5 pt-4 pb-3 h-full min-h-0';

/** Title + count cluster in collapsed strip — not pushed to strip bottom. */
export const kanbanColumnCollapsedClusterClass =
  'flex flex-col items-center gap-2 shrink-0';

export const kanbanColumnCollapsedTitleClass =
  'text-xs font-semibold text-dark-invirtus tracking-wide whitespace-nowrap';

export const kanbanColumnHeaderStartClass =
  'flex items-center gap-1.5 min-w-0 flex-1';

export const kanbanColumnHeaderActionsClass =
  'flex items-center gap-0.5 shrink-0';

/**
 * Hover-revealed controls (collapse + drag). Space is always reserved so the
 * header does not jump. Visible on column hover/focus-within, and always on
 * coarse/touch pointers that lack hover.
 */
export const kanbanColumnHeaderHoverControlsClass =
  'flex items-center gap-0.5 opacity-0 pointer-events-none transition-opacity duration-150 ' +
  'group-hover/column:opacity-100 group-hover/column:pointer-events-auto ' +
  'group-focus-within/column:opacity-100 group-focus-within/column:pointer-events-auto ' +
  '[@media(hover:none)]:opacity-100 [@media(hover:none)]:pointer-events-auto';

/** Shared checkbox column — header + cards must share this width. */
export const kanbanSelectionGutterClass =
  'w-4 shrink-0 flex items-center justify-center';

export const kanbanColumnCountClass =
  'inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-semibold tabular-nums bg-white/90 text-dark-invirtus/65 border border-dark-invirtus-soft/80';

export const kanbanColumnCountOverClass =
  'inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-semibold tabular-nums bg-red-soft text-red border border-red/20';

/**
 * Card list gap is fixed — never tied to density.
 * Horizontal padding uses --kanban-body-px so header/cards share one gutter.
 */
export const kanbanColumnBodyClass =
  'flex-1 min-h-[120px] pb-3 space-y-2 overflow-y-auto max-h-[calc(100vh-280px)] px-[var(--kanban-body-px)]';

export function getKanbanSortableItemClass(
  isDragging: boolean | undefined,
  isDragSourceCollapsed = false
): string {
  return cn(
    // Click opens details; a short pointer move starts drag.
    'cursor-pointer touch-none',
    isDragging && 'cursor-grabbing',
    // Own slot targeted (and on pickup): the source itself is the placeholder.
    isDragging && !isDragSourceCollapsed && kanbanFadedCardPlaceholderClass,
    // Other slot targeted: collapse — insertion spacer owns the footprint.
    isDragging &&
      isDragSourceCollapsed &&
      'opacity-0 !h-0 !min-h-0 !max-h-0 !overflow-hidden !m-0 !p-0 !border-0 pointer-events-none'
  );
}

/**
 * Density only affects card internals. Sets CSS vars for checkbox alignment:
 * header pl = body-px + card-px so select-all lines up with in-card checkboxes.
 */
export function getKanbanDensityShellClass(density: KanbanDensity): string {
  if (density === 'compact') {
    return cn(
      '[--kanban-body-px:0.625rem] [--kanban-card-px:0.5rem] [--kanban-card-py:0.375rem]',
      '[&_[data-kanban-card]]:py-[var(--kanban-card-py)] [&_[data-kanban-card]]:px-[var(--kanban-card-px)]'
    );
  }
  if (density === 'detailed') {
    return cn(
      '[--kanban-body-px:0.625rem] [--kanban-card-px:0.75rem] [--kanban-card-py:0.75rem]',
      '[&_[data-kanban-card]]:py-[var(--kanban-card-py)] [&_[data-kanban-card]]:px-[var(--kanban-card-px)]'
    );
  }
  return cn(
    '[--kanban-body-px:0.625rem] [--kanban-card-px:0.75rem] [--kanban-card-py:0.75rem]',
    '[&_[data-kanban-card]]:py-[var(--kanban-card-py)] [&_[data-kanban-card]]:px-[var(--kanban-card-px)]'
  );
}

/** Header left pad = body pad + card pad so checkbox x matches cards. */
export const kanbanColumnHeaderPadClass =
  'pl-[calc(var(--kanban-body-px)+var(--kanban-card-px))] pr-[var(--kanban-body-px)]';

/** Held card keeps the card's own resting shadow, no tilt. */
export const kanbanOverlayLiftClass = 'cursor-grabbing pointer-events-none';

export const KANBAN_CARD_OVERLAY_WIDTH = 256;

export const KANBAN_COLUMN_OVERLAY_WIDTH = 272;

export const kanbanBoardToolbarClass =
  'flex flex-wrap items-center gap-2.5 px-4 py-2.5 border-b border-dark-invirtus-soft shrink-0 bg-white';

export const kanbanViewsRowClass =
  'flex flex-wrap items-center gap-1.5 px-4 pt-2.5 pb-0';

export const kanbanSwimlaneLabelClass =
  'px-4 py-2.5 sticky left-0 z-[1] bg-white/95 backdrop-blur-sm border-b border-dark-invirtus-soft';

/** Avoid focus-ring clipping inside dialogs (use with Input). */
export const kanbanDialogInputClass =
  'focus:ring-2 focus:ring-purple/35 focus:border-purple focus:outline-none';

export const kanbanColumnDragPreviewClass =
  'flex flex-col rounded-2xl bg-dark-invirtus-soft/35 border border-blue/40 shadow-xl w-full h-[min(420px,70vh)] min-h-[280px] overflow-hidden pointer-events-none';

/**
 * Destination faded-card preview. Must be the only layout spacer while dragging
 * (sortable item transforms are disabled in KanbanSortableItem).
 */
export const kanbanFadedCardPlaceholderClass =
  'pointer-events-none select-none opacity-40';

/** Shared accent for insert line + `+` (same CSS variable / token). */
export const kanbanColumnInsertAccentVarClass =
  '[--kanban-insert-accent:var(--color-blue)]';

/**
 * Fixed layout spacer between columns when columnManage (matches former `gap-3`).
 * Width never changes on hover — affordance is an absolute overlay inside.
 */
export const kanbanColumnInsertZoneClass =
  'group/insert relative w-3 shrink-0 self-stretch';

/**
 * Absolute overlay hit region centered on the fixed gap.
 * Wider than the spacer for easier targeting; does not affect flex layout.
 * Inert until the zone is armed so cursor + click turn on with the `+`, never before.
 */
export const kanbanColumnInsertHitClass =
  'pointer-events-none absolute inset-y-0 left-1/2 z-[5] -ml-3.5 flex w-7 ' +
  'cursor-default items-center justify-center rounded-sm ' +
  'group-data-[armed=true]/insert:pointer-events-auto group-data-[armed=true]/insert:cursor-pointer ' +
  'group-focus-within/insert:pointer-events-auto group-focus-within/insert:cursor-pointer ' +
  '[@media(hover:none)]:pointer-events-auto [@media(hover:none)]:cursor-pointer ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--kanban-insert-accent)]/50';

/** Opacity-only reveal (~150–200ms). No width/transform animation. */
export const kanbanColumnInsertAffordanceClass =
  'pointer-events-none relative flex h-full w-full items-center justify-center ' +
  'opacity-0 transition-opacity duration-150 ease-out ' +
  'group-data-[armed=true]/insert:opacity-100 ' +
  'group-focus-within/insert:opacity-100 ' +
  '[@media(hover:none)]:opacity-100';

/** Centered by layout (auto margins), not transform, so the dashes snap to whole pixels. */
export const kanbanColumnInsertLineClass =
  'absolute inset-y-3 inset-x-0 mx-auto w-0.5 ' +
  'border-l-2 border-dashed border-[var(--kanban-insert-accent)]';

/** Filled circle interrupting the line — same accent; decorative (parent is the control). */
export const kanbanColumnInsertIconClass =
  'relative z-[1] inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ' +
  'bg-[var(--kanban-insert-accent)] text-dark-invirtus shadow-sm';

/**
 * Trello-style trailing add control: column-width, top-aligned with headers,
 * subtle fill so it reads as an empty list slot rather than a toolbar button.
 */
export const kanbanTrailingAddColumnClass =
  'group/trailing-add flex shrink-0 self-start items-center gap-2 ' +
  'min-w-[272px] w-[272px] max-w-[300px] h-10 px-3 rounded-2xl ' +
  'border border-dashed border-dark-invirtus-soft/80 ' +
  'bg-dark-invirtus-soft/40 text-dark-invirtus/70 ' +
  'transition-colors duration-150 ' +
  'hover:bg-dark-invirtus-soft/55 hover:text-dark-invirtus ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue/40 ' +
  'disabled:opacity-50 disabled:pointer-events-none';
